'use client';

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, useTransition } from 'react';
import { useLocale } from 'next-intl';
import { addToCart, getCart, removeCartLine, setCartAnalyticsAttributes, updateCartLine } from '@/lib/shopify/cart';
import { defaultVariant } from '@/lib/shopify/format';
import {
  ANALYTICS_CONSENT_EVENT,
  collectCartAttributes,
  isAnalyticsAttribute,
  sameAttributes,
  trackAddToCart,
  trackBeginCheckout,
  trackRemoveFromCart,
} from '@/lib/analytics';
import { MARKETING_CONSENT_EVENT } from '@/lib/meta-pixel';
import { cartContradictsPromo, promoWindowOpen, salePriceFor } from '@/config/promo';
import type { Cart, CartResult, Money, Product, ProductVariant } from '@/lib/shopify/types';
import { flyBean, prefersReducedMotion } from './beanFlight';

type Notice = 'error' | 'warning' | null;

/** Feedback of the add-to-cart buttons: 'added' lasts ~1 s, 'error' a few seconds. */
export type AddState = 'idle' | 'adding' | 'added' | 'error';

/** What the site announces for Black Friday. Prices charged are always Shopify's (see config/promo.ts). */
export type PromoState = {
  /** Sale price to announce for the selected variant, or null (promo off, ended, or Shopify contradicts it). */
  salePrice: Money | null;
  /** Free shipping may be announced (promo window open). Applies only to the countries in config/promo.ts. */
  freeShipping: boolean;
};

type ShopContextValue = {
  /** Product from Shopify, or null if it could not be loaded (the page still works). */
  product: Product | null;
  promo: PromoState;
  selectedVariant: ProductVariant | null;
  selectVariant: (variantId: string) => void;
  /** Picks the variant that matches the current options with one option changed. */
  chooseOption: (name: string, value: string) => void;
  cart: Cart | null;
  isCartOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  isPending: boolean;
  notice: Notice;
  /**
   * Adds the selected variant (or the given one) to the Shopify cart.
   * With `origin` (the button pressed): once Shopify confirms, a coffee bean flies from the button
   * to the header bag, the count updates when it lands and the cart drawer slides in, ready to
   * check out. Without it: opens the drawer straight away.
   * Resolves to true when Shopify confirmed the add.
   */
  add: (variantId?: string, quantity?: number, origin?: HTMLElement | null) => Promise<boolean>;
  addState: AddState;
  /** Increments each time an added item lands in the bag (drives the bag's micro-animation). */
  cartBump: number;
  /** Variant just added from a button: highlighted in the drawer while it stays open. */
  justAddedId: string | null;
  /** Adds the selected variant to the Shopify cart and goes straight to Shopify Checkout. */
  buyNow: () => void;
  /** Runs a fresh analytics-attribute sync and waits for it (max ~4 s) before going to Shopify. */
  prepareCheckout: (cart?: Cart) => Promise<void>;
  isRedirecting: boolean;
  setQuantity: (lineId: string, quantity: number) => void;
  removeLine: (lineId: string) => void;
};

const ShopContext = createContext<ShopContextValue | null>(null);

export function useShop() {
  const value = useContext(ShopContext);
  if (!value) throw new Error('useShop must be used inside <ShopProvider>');
  return value;
}

export default function ShopProvider({
  product,
  children,
}: {
  product: Product | null;
  children: React.ReactNode;
}) {
  const locale = useLocale();
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(
    () => (product ? defaultVariant(product)?.id ?? null : null)
  );
  const [cart, setCart] = useState<Cart | null>(null);
  const [isCartOpen, setCartOpen] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);
  const [isPending, startTransition] = useTransition();

  // Restore the cart saved in the cookie (survives reloads and language changes)
  useEffect(() => {
    let active = true;
    getCart(locale).then((c) => active && setCart(c));
    return () => {
      active = false;
    };
  }, [locale]);

  const selectedVariant = useMemo(
    () => product?.variants.find((v) => v.id === selectedVariantId) ?? null,
    [product, selectedVariantId]
  );

  const chooseOption = useCallback(
    (name: string, value: string) => {
      const wanted = (selectedVariant?.selectedOptions ?? []).map((o) =>
        o.name === name ? { name, value } : o
      );
      const match = product?.variants.find((v) =>
        wanted.every((w) => v.selectedOptions.some((o) => o.name === w.name && o.value === w.value))
      );
      if (match) setSelectedVariantId(match.id);
    },
    [product, selectedVariant]
  );

  const apply = useCallback((result: CartResult) => {
    if (result.cart) setCart(result.cart);
    setNotice(result.error ? 'error' : result.warning ? 'warning' : null);
  }, []);

  const [addState, setAddState] = useState<AddState>('idle');
  const [cartBump, setCartBump] = useState(0);
  const addStateTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const addingRef = useRef(false);
  const [justAddedId, setJustAddedId] = useState<string | null>(null);
  const openTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const settleAddState = (state: AddState, ms: number) => {
    setAddState(state);
    clearTimeout(addStateTimer.current);
    addStateTimer.current = setTimeout(() => setAddState('idle'), ms);
  };
  useEffect(
    () => () => {
      clearTimeout(addStateTimer.current);
      clearTimeout(openTimer.current);
    },
    []
  );

  const add = useCallback(
    async (variantId?: string, quantity = 1, origin?: HTMLElement | null) => {
      const id = variantId ?? selectedVariant?.id;
      const variant = product?.variants.find((v) => v.id === id);
      if (!id || !variant?.availableForSale) return false;

      // No button to animate from (header "Buy"): classic behaviour, the drawer shows the result
      if (!origin) {
        setCartOpen(true);
        startTransition(async () => {
          const result = await addToCart(id, quantity, locale);
          apply(result);
          if (result.cart && !result.error && product) trackAddToCart(product, variant, quantity, result.cart);
        });
        return true;
      }

      if (addingRef.current) return false;
      addingRef.current = true;
      clearTimeout(addStateTimer.current);
      setAddState('adding');
      setNotice(null);
      const result = await addToCart(id, quantity, locale).catch((): CartResult => ({ cart: null, warning: false, error: true }));

      // Shopify refused or failed: no animation, count and previous cart untouched, message by the button
      if (result.error || !result.cart) {
        addingRef.current = false;
        setNotice('error');
        settleAddState('error', 4000);
        return false;
      }

      // Confirmed by Shopify. The bean flies first; the real cart (and its count) lands with it.
      if (!prefersReducedMotion()) await flyBean(origin, document.querySelector('[data-cart-icon]'));
      setCart(result.cart);
      if (product) trackAddToCart(product, variant, quantity, result.cart);
      setCartBump((n) => n + 1);
      setNotice(result.warning ? 'warning' : null);
      setJustAddedId(id);
      addingRef.current = false;
      settleAddState('added', 1000);
      // A beat for the bag's pulse, then the drawer slides in to buy right away
      clearTimeout(openTimer.current);
      openTimer.current = setTimeout(() => setCartOpen(true), prefersReducedMotion() ? 0 : 240);
      return true;
    },
    [apply, locale, product, selectedVariant]
  );

  // ---- Analytics / marketing attributes of the Shopify cart (GA4 ids, Meta browser ids, site, UTMs) ----
  // Always mirrors the CURRENT consent: with consent they are saved, without it any analytics
  // attribute is removed. Runs one at a time (a chain), whenever a cart loads or changes, when the
  // consent changes and, awaited, right before going to checkout. Failures never affect the cart.
  const cartRef = useRef<Cart | null>(null);
  cartRef.current = cart;
  const syncChain = useRef<Promise<void>>(Promise.resolve());
  const syncAttributes = useCallback((override?: Cart): Promise<void> => {
    const run = async () => {
      try {
        const current = override ?? cartRef.current;
        if (!current) return;
        const desired = await collectCartAttributes(); // null unless consent is 'granted'
        const have = current.attributes.filter(isAnalyticsAttribute);
        if (sameAttributes(have, desired ?? [])) return;
        if (await setCartAnalyticsAttributes(desired)) {
          setCart((c) =>
            c && c.id === current.id
              ? { ...c, attributes: [...c.attributes.filter((a) => !isAnalyticsAttribute(a)), ...(desired ?? [])] }
              : c
          );
        }
      } catch {
        /* the cart keeps working without attribution */
      }
    };
    syncChain.current = syncChain.current.then(run, run);
    return syncChain.current;
  }, []);

  const prepareCheckout = useCallback(
    async (override?: Cart) => {
      await Promise.race([syncAttributes(override), new Promise<void>((resolve) => setTimeout(resolve, 4000))]);
    },
    [syncAttributes]
  );

  useEffect(() => {
    if (cart) void syncAttributes();
  }, [cart, syncAttributes]);

  useEffect(() => {
    const onConsent = () => void syncAttributes();
    window.addEventListener(ANALYTICS_CONSENT_EVENT, onConsent);
    window.addEventListener(MARKETING_CONSENT_EVENT, onConsent);
    return () => {
      window.removeEventListener(ANALYTICS_CONSENT_EVENT, onConsent);
      window.removeEventListener(MARKETING_CONSENT_EVENT, onConsent);
    };
  }, [syncAttributes]);

  const [isRedirecting, setRedirecting] = useState(false);
  const buyNow = useCallback(() => {
    const variant = selectedVariant;
    if (!variant?.availableForSale) return;
    startTransition(async () => {
      const result = await addToCart(variant.id, 1, locale);
      if (result.cart && !result.error) {
        setCart(result.cart);
        if (product) trackAddToCart(product, variant, 1, result.cart);
        trackBeginCheckout(result.cart);
        // The attributes must be in the cart (or cleared, without consent) before leaving for Shopify
        await prepareCheckout(result.cart);
        setRedirecting(true);
        window.location.href = result.cart.checkoutUrl; // official Shopify Checkout
      } else {
        apply(result);
        setCartOpen(true); // show the error in the cart
      }
    });
  }, [apply, locale, prepareCheckout, product, selectedVariant]);

  // remove_from_cart only after Shopify confirms the change, with the real difference in units
  const trackRemoval = useCallback((before: Cart | null, lineId: string, result: CartResult) => {
    if (!before || !result.cart || result.error) return;
    const line = before.lines.find((l) => l.id === lineId);
    if (!line) return;
    const after = result.cart.lines.find((l) => l.id === lineId);
    trackRemoveFromCart(line, line.quantity - (after?.quantity ?? 0), product && line.merchandise.product.handle === product.handle ? product.id : undefined);
  }, [product]);

  const setQuantity = useCallback(
    (lineId: string, quantity: number) =>
      startTransition(async () => {
        const result = await updateCartLine(lineId, quantity, locale);
        apply(result);
        trackRemoval(cart, lineId, result);
      }),
    [apply, cart, locale, trackRemoval]
  );

  const removeLine = useCallback(
    (lineId: string) =>
      startTransition(async () => {
        const result = await removeCartLine(lineId, locale);
        apply(result);
        trackRemoval(cart, lineId, result);
      }),
    [apply, cart, locale, trackRemoval]
  );

  // Black Friday: announce the sale price only while Shopify's own cart agrees with it. Once a cart
  // contradicts it, the announcement stays off for this page (never show a figure the checkout won't honour).
  const [promoContradicted, setPromoContradicted] = useState(false);
  useEffect(() => {
    if (cart && cartContradictsPromo(cart, product)) setPromoContradicted(true);
  }, [cart, product]);
  const windowOpen = promoWindowOpen();
  const promo: PromoState = {
    salePrice: windowOpen && !promoContradicted ? salePriceFor(selectedVariant) : null,
    freeShipping: windowOpen,
  };

  const value: ShopContextValue = {
    product,
    promo,
    selectedVariant,
    selectVariant: setSelectedVariantId,
    chooseOption,
    cart,
    isCartOpen,
    openCart: () => setCartOpen(true),
    closeCart: () => {
      setCartOpen(false);
      setNotice(null);
      setJustAddedId(null);
    },
    isPending,
    notice,
    add,
    addState,
    cartBump,
    justAddedId,
    buyNow,
    prepareCheckout,
    isRedirecting,
    setQuantity,
    removeLine,
  };

  return <ShopContext.Provider value={value}>{children}</ShopContext.Provider>;
}
